// Shared cosmetic-pack preview primitives used by both the
// /store cosmetics tab and the season-voucher portrait picker.
// Keeping them in one place means the two surfaces stay visually
// identical and a tweak to the fan / pile / playmat animation lands
// everywhere at once.

import { Component } from 'preact';
import { buildTokenImageUrl } from '../utils/arena/customTokenUrl';
import { isBasicSite } from '../utils/sorcery/basicSites';
import { getLocalApiOrigin } from '../utils/localApi';
import { isWeb } from '../utils/platform';
import { protectedMatVariantUrl } from '../utils/arena/cosmeticsCache';
import { t } from '../localization/t';
import { DICE_SKINS } from '../utils/game/dice3d/diceSkins.data';
import { sorceryImageUrl } from '../utils/cardImageUrl';
import { shelfPreviewUrl } from '../utils/game/dice3d/diceSetRig';
import {
  GOLD, ACCENT_GOLD, TEXT_MUTED, TEXT_PRIMARY,
  GOLD_BTN, PANEL_BG, PANEL_BORDER, CornerPlating, RADIUS,
} from '../lib/medievalTheme';

// Match a normalized sorcery card against a pack. Portrait packs match
// the pack's `keyword` against the card's `type` or `traits` array
// (excluding Site cards. Those belong to background packs). Background
// packs match Site cards whose `rarity` equals the pack's `rarity`.
export function cardMatchesPack(card, pack) {
  if (!card || !pack) return false;
  if (pack.kind === 'background') {
    if (isBasicSite(card.unique_id)) return false;
    return card.type === 'Site' && card.rarity === pack.rarity;
  }
  if (!pack.keyword) return false;
  if (card.type === 'Site') return false;
  if (card.type === pack.keyword) return true;
  const traits = Array.isArray(card.traits) ? card.traits : [];
  const k = pack.keyword.toLowerCase();
  return traits.some((t) => typeof t === 'string' && t.toLowerCase() === k);
}

export function getPackPool(sorceryCards, pack) {
  if (pack?.kind === 'playmat' || pack?.kind === 'token' || pack?.kind === 'dice') {
    if (!pack.cosmeticId) return [];
    return [{ unique_id: pack.cosmeticId, name: pack.name, _pack: pack }];
  }
  if (!Array.isArray(sorceryCards)) return [];
  return sorceryCards.filter((c) => cardMatchesPack(c, pack));
}

// Resolve a playmat image URL at one of two downscaled sizes:
//   'preview' (~480px). Store grid tiles + picker dropdown thumbnails.
//   'medium'  (~1100px). The store sidebar's large selected preview,
//                         where a 480px thumbnail looks soft.
// Never returns the full-resolution art (that's the in-game table mat).
export function getPlaymatPreviewUrl(pack, size = 'preview') {
  if (!pack) return null;
  const variant = size === 'medium' ? 'medium' : 'preview';
  // Protected playmats are fetched from the local /game-assets/
  // protected-playmat/<cosmeticId> endpoint, which the main process
  // resolves via the registry pushed at sign-in. The art never ships
  // in the bundle; the ?size param picks the downscaled variant.
  if (pack.protected && pack.cosmeticId) {
    // Web build: no local Electron server to downscale at serve time.
    // Load the pre-baked CDN variant published next to the master by
    // scripts/upload-playmats-to-r2.js.
    if (isWeb()) return protectedMatVariantUrl(pack.blobUrl, variant);
    return `${getLocalApiOrigin()}/game-assets/protected-playmat/${encodeURIComponent(pack.cosmeticId)}?size=${variant}`;
  }
  if (!pack.previewFile) return null;
  // Bundled mats: an `art-only/<name>` previewFile maps to the pre-baked
  // `previews/` (480px) or `medium/` (1100px) variant; otherwise fall
  // through to the literal path.
  const pf = pack.previewFile;
  const dir = variant === 'medium' ? 'medium' : 'previews';
  const variantPath = pf.startsWith('art-only/')
    ? `${dir}/${pf.slice('art-only/'.length)}`
    : pf;
  return `${getLocalApiOrigin()}/game-assets/playmats/${variantPath}`;
}

export function getTokenPreviewUrl(pack) {
  return buildTokenImageUrl(pack, getLocalApiOrigin());
}

// A dice pack previews with the art of the card the skin was cut from,
// which is what the shopper recognises: the baked tile is that same art
// blurred and swirled past recognition, so it reads as a colour smear on a
// store shelf. The tile stands in only if a recipe has no `source`.
// `null` when this build has no recipe under that id at all, which happens
// whenever the server's catalog runs ahead of the client the player is on.
// Callers fall back to the swatch.
export function getDiceSkinPreviewUrl(pack) {
  const skin = DICE_SKINS[pack?.diceSkinId];
  if (!skin) return null;
  return skin.source ? sorceryImageUrl(skin.source, getLocalApiOrigin()) : skin.tile || null;
}

// The flat colour to stand in for a skin with no tile to show.
export function getDiceSkinSwatch(pack) {
  return DICE_SKINS[pack?.diceSkinId]?.swatch || pack?.accentColor || null;
}

// Picks N preview cards from a pack's pool deterministically: sorts by
// unique_id then samples evenly across the result. The same N cards
// always show for a given pool, with no per-render randomness.
export function pickStablePreviewCards(pool, n) {
  const withArt = pool.filter((c) => c.printings?.[0]?.image_url);
  if (withArt.length === 0) return [];
  const sorted = [...withArt].sort((a, b) =>
    (a.unique_id || '').localeCompare(b.unique_id || '')
  );
  if (sorted.length <= n) return sorted;
  const picks = [];
  for (let i = 0; i < n; i++) {
    picks.push(sorted[Math.floor((i + 0.5) * sorted.length / n)]);
  }
  return picks;
}

// Three-layer pile for Realm/Background pack previews.
export const REALM_PILE_LAYERS = [
  { rot: -11, dx: -7, dy: 5, scale: 0.90, opacity: 0.78, z: 1, hRot: -18, hDx: -22, hDy: 8, hScale: 0.93 },
  { rot: 7,   dx: 6,  dy: 3, scale: 0.95, opacity: 0.90, z: 2, hRot: 12,  hDx: 20,  hDy: 5, hScale: 0.97 },
  { rot: -2,  dx: 0,  dy: 0, scale: 1.00, opacity: 1.00, z: 3, hRot: -2,  hDx: 0,   hDy: -3, hScale: 1.04 },
];

// Five-layer fan for Portrait pack previews.
export const PORTRAIT_FAN_LAYERS = [
  { rot: -22, scale: 0.86, opacity: 0.80, z: 1, hRot: -34, hScale: 0.92 },
  { rot: -11, scale: 0.93, opacity: 0.92, z: 2, hRot: -17, hScale: 0.97 },
  { rot: 0,   scale: 1.00, opacity: 1.00, z: 3, hRot: 0,   hScale: 1.06 },
  { rot: 11,  scale: 0.93, opacity: 0.92, z: 2, hRot: 17,  hScale: 0.97 },
  { rot: 22,  scale: 0.86, opacity: 0.80, z: 1, hRot: 34,  hScale: 0.92 },
];

export const PREVIEW_TRANSITION = 'transform 320ms cubic-bezier(0.22, 0.9, 0.3, 1), opacity 220ms ease-out';

// Standard tile footprints used by store + voucher modal. Importing
// these (rather than hardcoding cqw values per call site) keeps the
// tiles the same physical size everywhere they're rendered.
export const PORTRAIT_TILE_SIZE = { width: '14cqw', height: '19.55cqw' };
export const LANDSCAPE_TILE_SIZE = { width: '19.55cqw', height: '14cqw' };

export function RealmStackPreview({ cards, hovered }) {
  const slots = REALM_PILE_LAYERS.map((_, i) => cards[i % cards.length]);
  return (
    <div className="absolute inset-0">
      {REALM_PILE_LAYERS.map((layer, i) => {
        const url = slots[i]?.printings?.[0]?.image_url;
        if (!url) return null;
        const rot = hovered ? layer.hRot : layer.rot;
        const dx = hovered ? layer.hDx : layer.dx;
        const dy = hovered ? layer.hDy : layer.dy;
        const scale = hovered ? layer.hScale : layer.scale;
        return (
          <img
            key={i}
            src={url}
            alt=""
            draggable={false}
            className="absolute pointer-events-none"
            style={{
              left: '50%',
              top: '50%',
              width: '52%',
              height: 'auto',
              transform: `translate(-50%, -50%) translate(${dx}%, ${dy}%) rotate(${90 + rot}deg) scale(${scale})`,
              opacity: layer.opacity,
              zIndex: layer.z,
              borderRadius: RADIUS.card,
              boxShadow: '0 8px 18px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,0,0,0.45)',
              transition: PREVIEW_TRANSITION,
            }}
          />
        );
      })}
    </div>
  );
}

// Stateful so we can render a skeleton while the preview image streams
// in. Protected playmats (Vercel-Blob-backed) take a beat on first
// fetch. Main-process downscale + cache. And an empty <img> looked
// like a broken tile.
//
// `img.complete` is checked synchronously on mount: if the image is
// already in the HTTP cache, `complete` is true before componentDidMount
// runs, so we flip to loaded:true before the browser paints and the
// skeleton never flashes on a cache hit.
//
// Props:
//   url     image source (preview or medium variant)
//   hovered scales the art up slightly (grid hover)
//   fill    true  => art fills its container 100% (the caller clips +
//                    frames it. Used by the store grid tiles)
//           false => art floats at 92% with its own rounded corners +
//                    drop shadow (used by the sidebar)
// A protected playmat preview can momentarily 404 `not-registered` if
// its <img> fires before the registry PUT lands (notably the first-run
// picker, which renders before the hub). The endpoint self-heals within
// a beat, so retry a few times. Staying on the skeleton. Before
// conceding to the "unavailable" frame. A retry nonce on the URL forces
// the browser to re-issue the request instead of replaying the cached
// 404. Bundled native mats never hit this path; a genuinely missing file
// just exhausts the retries and shows the frame, same as before.
const PREVIEW_MAX_RETRIES = 5;
const PREVIEW_RETRY_DELAY_MS = 500;

export class PlaymatPreview extends Component {
  state = { loaded: false, failed: false, attempt: 0 };

  setImgRef = (el) => { this.imgEl = el; };

  componentDidMount() {
    if (this.imgEl?.complete && this.imgEl.naturalWidth > 0) {
      this.setState({ loaded: true });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.url !== this.props.url) {
      const cached = this.imgEl?.complete && this.imgEl.naturalWidth > 0;
      clearTimeout(this._retryTimer);
      this.setState({ loaded: !!cached, failed: false, attempt: 0 });
    }
  }

  componentWillUnmount() {
    clearTimeout(this._retryTimer);
  }

  handleLoad = () => { this.setState({ loaded: true, failed: false }); };

  handleError = () => {
    if (this.state.attempt < PREVIEW_MAX_RETRIES) {
      this._retryTimer = setTimeout(() => {
        this.setState((s) => ({ attempt: s.attempt + 1, failed: false }));
      }, PREVIEW_RETRY_DELAY_MS);
    } else {
      this.setState({ failed: true });
    }
  };

  render() {
    const { url, hovered, fill = false } = this.props;
    const { loaded, failed, attempt } = this.state;
    if (!url) {
      return <div className="w-full h-full flex items-center justify-center text-[0.625cqw]" style={{ color: TEXT_MUTED }}>?</div>;
    }
    // Clean URL on the first try (hits the warm HTTP cache); a retry
    // nonce only on re-attempts so a cached 404 doesn't replay.
    const src = attempt === 0 ? url : `${url}${url.includes('?') ? '&' : '?'}_retry=${attempt}`;
    const dim = fill ? '100%' : '92%';
    const radius = fill ? '0' : '0.5cqw';
    if (failed) {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="absolute flex items-center justify-center text-center px-2 text-[0.6cqw]"
            style={{
              width: dim,
              height: dim,
              borderRadius: radius,
              background: `url("/tex-noise.webp"), linear-gradient(135deg, rgba(30,24,16,0.96) 0%, rgba(16,12,8,0.96) 100%)`,
              border: `1px solid ${GOLD} 0.12)`,
              boxShadow: fill ? 'none' : 'inset 0 1px 0 rgba(255,220,140,0.05), 0 10px 22px rgba(0,0,0,0.7)',
              color: TEXT_MUTED,
            }}
          >
            {t('Artwork unavailable')}
          </div>
        </div>
      );
    }
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        {!loaded && (
          <div
            className="absolute overflow-hidden"
            style={{
              width: dim,
              height: dim,
              borderRadius: radius,
              // Carved-stone placeholder: tiled noise over a warm-dark
              // gradient, with a slow gold sheen sweeping across.
              background: `url("/tex-noise.webp"), linear-gradient(135deg, rgba(30,24,16,0.96) 0%, rgba(16,12,8,0.96) 100%)`,
              border: `1px solid ${GOLD} 0.12)`,
              boxShadow: fill ? 'none' : 'inset 0 1px 0 rgba(255,220,140,0.05), 0 10px 22px rgba(0,0,0,0.7)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(105deg, transparent 35%, rgba(232,213,160,0.12) 50%, transparent 65%)',
                animation: 'playmat-skeleton-sweep 1.6s ease-in-out infinite',
              }}
            />
            <style>{`@keyframes playmat-skeleton-sweep { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }`}</style>
          </div>
        )}
        <img
          ref={this.setImgRef}
          src={src}
          alt=""
          draggable={false}
          onLoad={this.handleLoad}
          onError={this.handleError}
          decoding="async"
          className="pointer-events-none"
          style={{
            width: dim,
            height: dim,
            objectFit: 'cover',
            borderRadius: radius,
            boxShadow: fill ? 'none' : '0 10px 22px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,0,0,0.45)',
            transform: hovered ? 'scale(1.06)' : 'scale(1)',
            opacity: loaded ? 1 : 0,
            transition: `${PREVIEW_TRANSITION}, opacity 0.25s ease-out`,
          }}
        />
      </div>
    );
  }
}

export function TokenCardPreview({ url, hovered }) {
  if (!url) {
    return <div className="w-full h-full flex items-center justify-center text-[0.625cqw]" style={{ color: TEXT_MUTED }}>?</div>;
  }
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <img
        src={url}
        alt=""
        draggable={false}
        className="pointer-events-none"
        style={{
          width: '92%',
          height: '92%',
          objectFit: 'contain',
          borderRadius: RADIUS.card,
          filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.7))',
          transform: hovered ? 'scale(1.06)' : 'scale(1)',
          transition: PREVIEW_TRANSITION,
        }}
      />
    </div>
  );
}

// A dice set as the store shows it, inside a 16:10 frame the caller
// positions: the source card's art cover-cropped to the band where a
// Sorcery card keeps its illustration, darkened toward the foot and lit from
// below in the skin's colour, and the set's baked still on top (the six dice
// at rest, from `bakeShelfPreview`). `stillHidden` fades the still out for
// the close-up, where the live dice take over the same picture.
export function DiceSetPreview({ pack, hovered, stillHidden = false }) {
  const art = getDiceSkinPreviewUrl(pack);
  const skin = DICE_SKINS[pack?.diceSkinId];
  const still = skin ? shelfPreviewUrl(skin) : null;
  const swatch = getDiceSkinSwatch(pack) || '#3a2a18';
  return (
    <>
      <div className="absolute inset-0 pointer-events-none" style={{ background: swatch }} />
      {art ? (
        <img
          src={art}
          alt=""
          draggable={false}
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{
            objectFit: 'cover',
            objectPosition: 'center 18%',
            filter: hovered ? 'saturate(1.05) brightness(0.9)' : 'saturate(0.9) brightness(0.72)',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
            transition: 'transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1), filter 0.3s ease-out',
          }}
        />
      ) : null}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 70% 55% at 50% 100%, ${swatch} 0%, transparent 70%), linear-gradient(180deg, rgba(8,6,4,0.15) 0%, rgba(8,6,4,0) 30%, rgba(8,6,4,0.5) 70%, rgba(8,6,4,0.86) 100%)`,
        }}
      />
      {still ? (
        <img
          src={still}
          alt=""
          draggable={false}
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ objectFit: 'fill', opacity: stillHidden ? 0 : 1, transition: 'opacity 260ms ease-out' }}
        />
      ) : null}
    </>
  );
}

// The shop's name overlay, shared with the owned-dice picker. Keep the
// label on the artwork with the exact same spacing, type and shadow.
export function DiceSetPreviewCaption({ pack, children }) {
  return (
    <div
      className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3"
      style={{ zIndex: 4, padding: '0.375cqw' }}
    >
      <div className="min-w-0 text-left" style={{ margin: '0 0 0.1cqw 0.25cqw' }}>
        <div
          className="truncate arena-heading text-[0.95cqw]"
          style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.95), 0 0 6px rgba(0,0,0,0.85), 0 0 14px rgba(0,0,0,0.6)' }}
        >
          {pack.name}
        </div>
      </div>
      {children}
    </div>
  );
}

// Shared reveal overlay used after a cosmetic purchase (store) or
// voucher redemption. `reveal` is an array of `{ pack, card }` entries
// One for each cosmetic that was just unlocked. And `onClose` fires
// when the user dismisses it. Visually a single tile reads as a single
// cosmetic; multiple tiles fan out so a multi-pack buy lands as one
// "look at all the loot" moment.
export function CosmeticRevealModal({ reveal, onClose }) {
  if (!reveal || reveal.length === 0) return null;
  const tileW = reveal.length <= 3 ? '12cqw' : reveal.length <= 5 ? '9.5cqw' : '7.5cqw';
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center"
      style={{ background: 'radial-gradient(ellipse 80% 70% at 50% 50%, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.94) 100%)' }}
      onClick={onClose}
    >
      <div
        className="relative flex flex-col items-center px-8 py-7 mx-4"
        style={{
          background: PANEL_BG,
          backdropFilter: 'blur(8px)',
          border: `1px solid ${PANEL_BORDER}`,
          borderRadius: 'var(--radius-lg)',
          boxShadow: `0 0 40px rgba(0,0,0,0.55), 0 0 24px ${GOLD} 0.08)`,
          maxWidth: '70cqw',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <CornerPlating position="top-left" />
        <CornerPlating position="top-right" />
        <CornerPlating position="bottom-left" />
        <CornerPlating position="bottom-right" />
        <div className="arena-heading text-[1.4cqw] mb-3" style={{ color: ACCENT_GOLD, textShadow: '0 0 14px rgba(232,196,102,0.35), 0 2px 6px rgba(0,0,0,0.9)' }}>
          {reveal.length === 1 ? t('Unlocked') : t('{n} Cosmetics Unlocked', { n: reveal.length })}
        </div>
        {/* Past a screen's worth the list scrolls; dice sets sit three to a row at this width. */}
        <div className="flex gap-3 flex-wrap items-end justify-center mb-4 max-w-[60cqw]" style={{ maxHeight: '58cqh', overflowY: 'auto' }}>
          {reveal.map(({ pack, card }, idx) => {
            const isPlaymat = pack.kind === 'playmat';
            const isToken = pack.kind === 'token';
            const isDice = pack.kind === 'dice';
            const playmatUrl = isPlaymat ? getPlaymatPreviewUrl(pack) : null;
            const tokenUrl = isToken ? getTokenPreviewUrl(pack) : null;
            const tileWidth = isPlaymat ? (reveal.length <= 3 ? '20cqw' : '15cqw') : isDice ? '18.5cqw' : tileW;
            const aspect = isPlaymat || isDice ? '16 / 10' : '63 / 88';
            return (
              <div key={`${pack.id}-${card.unique_id}-${idx}`} className="flex flex-col items-center" style={{ width: tileWidth }}>
                <div
                  className="relative overflow-hidden"
                  style={{
                    width: '100%',
                    aspectRatio: aspect,
                    borderRadius: isPlaymat || isDice ? 'var(--radius-md)' : RADIUS.card,
                    border: `1.5px solid ${ACCENT_GOLD}`,
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.6), 0 0 32px rgba(232, 196, 102, 0.45), 0 12px 28px rgba(0,0,0,0.55)',
                    animation: `revealIn 600ms cubic-bezier(0.2,0.9,0.25,1.05) ${idx * 90}ms both`,
 }}
 >
 {isPlaymat ? (
 playmatUrl ? (
 <img src={playmatUrl} alt={pack.name} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
 ) : (
 <div className="w-full h-full flex items-center justify-center text-[0.75cqw]" style={{ color: TEXT_MUTED }}>
 {pack.name}
 </div>
 )
 ) : isToken ? (
 tokenUrl ? (
 <img src={tokenUrl} alt={pack.tokenName || pack.name} draggable={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
 ) : (
 <div className="w-full h-full flex items-center justify-center text-[0.75cqw]" style={{ color: TEXT_MUTED }}>
 {pack.tokenName || pack.name}
 </div>
 )
 ) : isDice ? (
 <DiceSetPreview pack={pack} hovered />
 ) : card.printings?.[0]?.image_url ? (
 <img
 src={card.printings[0].image_url}
 alt={card.name}
 draggable={false}
 style={{ width: '100%', height: '100%', objectFit: 'cover' }}
 />
 ) : (
 <div className="w-full h-full flex items-center justify-center text-[0.75cqw]" style={{ color: TEXT_MUTED }}>
 {card.name}
 </div>
 )}
 </div>
 <div className="text-[0.6cqw] text-center mt-2 font-bold arena-heading truncate w-full" style={{ color: '#e8d5a0' }} title={isPlaymat || isDice ? pack.name : isToken ? (pack.tokenName || pack.name) : card.name}>
 {isPlaymat || isDice ? pack.name : isToken ? (pack.tokenName || pack.name) : card.name}
 </div>
 <div className="text-[0.5cqw] text-center italic" style={{ color: TEXT_MUTED }}>
 {isPlaymat ? t('Playmat') : isToken ? t('Token') : isDice ? t('Dice Set') : card.type === 'Site' ? t('Background') : t('Portrait')}
 </div>
 </div>
 );
 })}
 </div>
 <button
 type="button"
 className="mt-2 px-5 py-2 text-[0.75cqw] cursor-pointer transition-all hover:scale-[1.03] active:scale-[0.97]"
 style={{
 ...GOLD_BTN,
 borderRadius: 'var(--radius-md)',
 fontWeight: 700,
 }}
 onClick={onClose}
 >
 {t('Continue')}
 </button>
 <style>{`
          @keyframes revealIn {
            0% { opacity: 0; transform: translateY(18px) scale(0.92); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
        `}</style>
      </div>
    </div>
  );
}

export function PortraitFanPreview({ cards, hovered }) {
  const slots = PORTRAIT_FAN_LAYERS.map((_, i) => cards[i % cards.length]);
  return (
    <div className="absolute inset-0">
      {PORTRAIT_FAN_LAYERS.map((layer, i) => {
        const url = slots[i]?.printings?.[0]?.image_url;
        if (!url) return null;
        const rot = hovered ? layer.hRot : layer.rot;
        const scale = hovered ? layer.hScale : layer.scale;
        return (
          <img
            key={i}
            src={url}
            alt=""
            draggable={false}
            className="absolute pointer-events-none"
            style={{
              left: '50%',
              top: '50%',
              width: '58%',
              height: 'auto',
              transformOrigin: '50% 115%',
              transform: `translate(-50%, -50%) rotate(${rot}deg) scale(${scale})`,
              opacity: layer.opacity,
              zIndex: layer.z,
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 8px 16px rgba(0,0,0,0.65), 0 0 0 1px rgba(0,0,0,0.45)',
              transition: PREVIEW_TRANSITION,
            }}
          />
        );
      })}
    </div>
  );
}
